import React, { useState, useRef, useEffect, useMemo, use } from 'react';
import { useAuthStatus } from '../../../auth/useAuthStatus';
import { useBrand } from '../../../app/config/brand';
import AdminCompanyContext from '../../../shared/contexts/AdminCompanyContext';

const OrderAccountBanner: React.FC = () => {
  const { isPlatformUser } = useAuthStatus();
  const adminCompany = use(AdminCompanyContext);
  const brand = useBrand();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const companies = useMemo(() => adminCompany?.companies ?? [], [adminCompany?.companies]);
  const selectedCompanyId = adminCompany?.selectedCompanyId ?? null;
  const selectedCompany = adminCompany?.selectedCompany ?? null;
  const selectCompany = adminCompany?.selectCompany;
  const isLoading = adminCompany?.isLoading ?? false;

  // Filter companies by search
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const query = searchQuery.toLowerCase();
    return companies.filter(
      (c) =>
        c.displayName.toLowerCase().includes(query) ||
        (c.name && c.name.toLowerCase().includes(query)),
    );
  }, [companies, searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Not a platform user or context not available — render nothing
  if (!isPlatformUser || !adminCompany) return null;

  const handleSelect = (companyId: string) => {
    selectCompany?.(companyId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setSearchQuery('');
  };

  if (isLoading) {
    return (
      <div
        className="mb-4 rounded-lg border p-4 animate-pulse"
        style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight, height: 56 }}
      />
    );
  }

  // ── No org selected: prominent picker ─────────────────────────────────
  if (!selectedCompany) {
    return (
      <div
        className="mb-4 rounded-lg border-2 p-4"
        style={{
          borderColor: brand.colors.warning,
          backgroundColor: '#FFFBEB',
        }}
        data-testid="order-account-banner"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke={brand.colors.warning} strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
              Select a firm to create this order
            </span>
          </div>

          <div className="relative flex-1" ref={dropdownRef}>
            <button
              onClick={handleToggle}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border transition-colors hover:bg-white"
              style={{
                borderColor: isOpen ? brand.colors.primary : brand.colors.borderDark,
                backgroundColor: 'white',
              }}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
            >
              <span style={{ color: brand.colors.textLight }}>Choose a firm...</span>
              <svg
                className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke={brand.colors.textLight}
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isOpen && (
              <div
                className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border z-50"
                style={{ borderColor: brand.colors.border }}
                role="listbox"
              >
                {/* Search */}
                <div className="p-2 border-b" style={{ borderColor: brand.colors.border }}>
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search firms..."
                    className="w-full px-3 py-1.5 text-sm rounded-sm border focus:outline-hidden focus:ring-2 focus:ring-offset-0"
                    style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                  />
                </div>

                {/* Options */}
                <div className="max-h-48 overflow-y-auto py-1">
                  {filteredCompanies.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-center" style={{ color: brand.colors.textLight }}>
                      No firms found
                    </div>
                  ) : (
                    filteredCompanies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => handleSelect(company.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50"
                        role="option"
                        aria-selected={false}
                      >
                        <span style={{ color: brand.colors.text }}>{company.displayName}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Org selected: compact banner ──────────────────────────────────────
  return (
    <div
      className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between"
      style={{
        borderColor: brand.colors.border,
        backgroundColor: brand.colors.bgAccent,
      }}
      data-testid="order-account-banner"
    >
      <div className="flex items-center gap-2 text-sm">
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke={brand.colors.primary} strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span style={{ color: brand.colors.textLight }}>Acting on behalf of:</span>
        <span className="font-medium" style={{ color: brand.colors.text }}>
          {selectedCompany.displayName}
        </span>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleToggle}
          className="text-sm font-medium px-2 py-1 rounded transition-colors hover:bg-white/60"
          style={{ color: brand.colors.link }}
        >
          Change
        </button>

        {isOpen && (
          <div
            className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border z-50"
            style={{ borderColor: brand.colors.border, minWidth: 260 }}
            role="listbox"
          >
            {/* Search */}
            <div className="p-2 border-b" style={{ borderColor: brand.colors.border }}>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search firms..."
                className="w-full px-3 py-1.5 text-sm rounded-sm border focus:outline-hidden focus:ring-2 focus:ring-offset-0"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
              />
            </div>

            {/* Options */}
            <div className="max-h-48 overflow-y-auto py-1">
              {filteredCompanies.length === 0 ? (
                <div className="px-3 py-2 text-sm text-center" style={{ color: brand.colors.textLight }}>
                  No firms found
                </div>
              ) : (
                filteredCompanies.map((company) => {
                  const isSelected = selectedCompanyId === company.id;
                  return (
                    <button
                      key={company.id}
                      onClick={() => handleSelect(company.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      {isSelected && (
                        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke={brand.colors.primary} strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={isSelected ? '' : 'ml-6'} style={{ color: brand.colors.text }}>
                        {company.displayName}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderAccountBanner;
